import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import TopNav from '../components/sections/TopNav';
import SiteFooter from '../components/sections/SiteFooter';
import FoundingCountdownTicker from '../components/FoundingCountdownTicker';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const PLANS = [
  {
    key: 'monthly',
    label: 'Monthly',
    price: '$13',
    priceSuffix: 'AUD / month',
    tagline: 'Unlimited stories, videos, Rumour File and comments. Cancel any time.',
    accent: 'border-zinc-700',
  },
  {
    key: 'annual',
    label: 'Annual',
    price: '$130',
    priceSuffix: 'AUD / year',
    tagline: 'Everything Monthly gives you. $26 off paying month by month.',
    accent: 'border-red-600',
    ribbon: 'Best value',
  },
  {
    key: 'founding',
    label: 'Founding Member',
    price: '$249',
    priceSuffix: 'one-off',
    tagline:
      'Lifetime access to everything on the site, including anything we lock later. Your name on the Founders page. Never billed again. 80 places only.',
    accent: 'border-yellow-500',
    ribbon: '80 places',
  },
];

const Subscribe = () => {
  const { user, hasAccess } = useAuth();
  const navigate = useNavigate();
  const [slots, setSlots] = useState(null);
  const [config, setConfig] = useState(null);
  const [busy, setBusy] = useState('');
  const [founderName, setFounderName] = useState('');
  const [error, setError] = useState('');
  const foundingCardRef = useRef(null);

  const scrollToFounding = () => {
    if (!foundingCardRef.current) return;
    foundingCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Give it a heartbeat then focus the founder-name input so mobile keyboards pop
    setTimeout(() => {
      const input = document.querySelector('[data-testid="founder-display-name"]');
      if (input) input.focus();
    }, 500);
  };

  useEffect(() => {
    fetch(`${API}/api/billing/founding-slots`).then((r) => r.json()).then(setSlots).catch(() => {});
    fetch(`${API}/api/billing/config`).then((r) => r.json()).then(setConfig).catch(() => {});
  }, []);

  // If the visitor came from /login (or another page) with an intent stashed
  // in sessionStorage, scroll straight to that plan card so they land where
  // they were about to click. Fires once user + slots are loaded.
  useEffect(() => {
    if (!user || !slots) return;
    let intent = null;
    try { intent = sessionStorage.getItem('outlaw_intent_plan'); } catch { /* ignore */ }
    if (!intent) return;
    try { sessionStorage.removeItem('outlaw_intent_plan'); } catch { /* ignore */ }
    // Delay a tick so the card ref is attached.
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="plan-${intent}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (intent === 'founding') {
        const input = document.querySelector('[data-testid="founder-display-name"]');
        if (input) setTimeout(() => input.focus(), 500);
      }
    }, 250);
  }, [user, slots]);

  const startCheckout = async (plan) => {
    setError('');
    if (config && !config.configured) {
      setError('Payments are not live yet. Please contact ryan@outlawmedia.com.au to be invited.');
      return;
    }
    setBusy(plan);
    try {
      try {
        if (window.plausible) window.plausible('Checkout Started', { props: { plan } });
      } catch { /* analytics never blocks checkout */ }
      const res = await fetch(`${API}/api/billing/checkout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          price: plan,
          origin_url: window.location.origin,
          ...(plan === 'founding' ? { founder_display_name: founderName.trim().slice(0, 40) } : {}),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not start checkout.');
      // Stripe hosted checkout - full-page redirect.
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(err.message || 'Could not start checkout.');
      setBusy('');
    }
  };

  const foundingSoldOut = slots?.sold_out;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="subscribe-page">
      <TopNav />
      <main className="max-w-5xl mx-auto px-4 py-12">
        <p className="text-red-500 text-[11px] font-black uppercase tracking-[0.35em] mb-2">Subscribe to Outlaw Media</p>
        <h1 className="text-4xl sm:text-5xl font-black leading-tight mb-3" style={{ fontFamily: 'Georgia, serif' }}>
          Back independent Australian crime reporting.
        </h1>
        <p className="text-zinc-400 text-sm sm:text-base max-w-2xl mb-6">
          Reports are for subscribers only. Please choose your unlimited subscription plan.
        </p>

        {!user && (
          <div className="mb-8 rounded-lg border border-red-900/50 bg-zinc-950/70 p-4 sm:p-5 max-w-xl" data-testid="guest-checkout-note">
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-red-400">
              No sign-up first
            </p>
            <p className="text-sm text-zinc-300 mt-2">
              Pick a plan and you go straight to payment. Stripe takes your email, and the moment it
              clears we set up your account and send you a login link - nothing to fill in here.
            </p>
          </div>
        )}

        {/* Three-step clarity strip: gives first-time visitors an at-a-glance
            map of the transaction so nobody gets lost between login, checkout
            and reading. Hidden for users who already have access. */}
        {!hasAccess && (
          <>
            {!user && (
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded border border-zinc-800 bg-zinc-950/60 px-4 py-3" data-testid="subscribe-existing-login">
                <p className="text-zinc-300 text-sm">
                  <span className="text-white font-black">Already have an account?</span> Log in and your subscription picks up where you left off.
                </p>
                <Link
                  to="/login"
                  data-testid="subscribe-login-link"
                  className="inline-flex items-center gap-1.5 rounded bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 hover:border-red-500 px-3 py-2 text-[11px] font-black uppercase tracking-[0.25em] text-white whitespace-nowrap"
                >
                  Log in
                </Link>
              </div>
            )}
            <ol
              data-testid="subscribe-steps"
              className="mb-8 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm"
            >
            {[
              { n: 1, title: 'Pick a plan', body: 'Monthly, Annual or Founding, below.' },
              { n: 2, title: 'Pay with Stripe', body: user ? 'Signed in as you.' : 'Card and email, on Stripe. No password.' },
              { n: 3, title: 'Read + watch', body: user ? 'Instant access. Cancel any time.' : 'Account made for you, login link emailed.' },
            ].map((s) => (
              <li
                key={s.n}
                className="border border-zinc-800 rounded p-3 bg-zinc-950/60 flex items-start gap-3"
              >
                <span className="shrink-0 w-6 h-6 rounded-full bg-red-600 text-white text-xs font-black flex items-center justify-center">{s.n}</span>
                <div>
                  <p className="text-white font-black text-[13px] uppercase tracking-widest">{s.title}</p>
                  <p className="text-zinc-400 text-xs mt-0.5">{s.body}</p>
                </div>
              </li>
            ))}
          </ol>
          </>
        )}

        {/* Scarcity ticker - live "N of 80 places left" so Founding sells itself.
            Polls every 30s and flashes when a place is claimed while the
            visitor is on the page. Clicking scrolls to the Founding card. */}
        <FoundingCountdownTicker onClaimClick={scrollToFounding} />

        {/* One-account policy notice - explicit, unmissable, and repeated in
            the email ladder + Account page. Sits ABOVE the plan cards so no
            reader can later claim the terms weren't clear at signup. */}
        <div
          data-testid="subscribe-sharing-policy"
          className="mt-6 mb-8 rounded-lg border-2 border-red-600/70 bg-gradient-to-br from-red-950/50 via-black to-zinc-950 p-4 sm:p-5 shadow-[0_0_18px_rgba(220,38,38,0.25)]"
        >
          <p className="text-red-400 text-[10px] font-black uppercase tracking-[0.35em] mb-1.5">One account per person</p>
          <p className="text-white text-sm sm:text-base font-black leading-snug mb-2" style={{ fontFamily: 'Georgia, serif' }}>
            Any account sharing will result in your account being banned, without refund.
          </p>
          <p className="text-zinc-300 text-xs sm:text-sm leading-relaxed">
            You get up to <strong className="text-white">3 devices</strong> per account (phone + laptop + tablet) inside any 14-day window.
            Cross that limit and you get <strong className="text-yellow-300">3 chances</strong>: Warning 1 → Final Warning → permanent ban with
            <strong className="text-red-300"> no refund</strong>. Manage your active devices any time from your Account page.
          </p>
          <ol
            data-testid="subscribe-sharing-ladder"
            className="mt-3 flex flex-col sm:flex-row gap-2 text-[11px]"
          >
            <li className="flex-1 rounded border border-yellow-500/50 bg-yellow-950/30 px-3 py-2 text-yellow-200">
              <span className="font-black uppercase tracking-widest">1. Warning</span>
              <span className="block text-yellow-300/70 mt-0.5">First trip over the limit — email warning.</span>
            </li>
            <li className="flex-1 rounded border border-orange-500/50 bg-orange-950/30 px-3 py-2 text-orange-200">
              <span className="font-black uppercase tracking-widest">2. Final Warning</span>
              <span className="block text-orange-300/70 mt-0.5">One more new device and you&apos;re out.</span>
            </li>
            <li className="flex-1 rounded border border-red-500/60 bg-red-950/40 px-3 py-2 text-red-200">
              <span className="font-black uppercase tracking-widest">3. Ban · No Refund</span>
              <span className="block text-red-300/70 mt-0.5">Email blocked from re-signup.</span>
            </li>
          </ol>
        </div>

        {hasAccess && (
          <div className="mb-8 border border-emerald-700/70 bg-emerald-950/40 rounded px-4 py-3 text-sm">
            <p>
              You&apos;re already on <strong className="uppercase">{user?.plan}</strong> access.{' '}
              <Link to="/account" className="underline decoration-emerald-400">Manage billing →</Link>
            </p>
          </div>
        )}

        {config && !config.configured && (
          <div className="mb-8 border border-yellow-700/70 bg-yellow-950/40 rounded px-4 py-3 text-sm text-yellow-200" data-testid="stripe-not-configured">
            Payments are being wired up right now. You can create your account and we&apos;ll email you the moment checkout opens.
          </div>
        )}

        {error && (
          <div data-testid="subscribe-error" className="mb-6 border border-red-700 bg-red-950/40 rounded px-4 py-3 text-sm text-red-200">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {PLANS.map((p) => {
            const disabled = p.key === 'founding' && foundingSoldOut;
            return (
              <div
                key={p.key}
                ref={p.key === 'founding' ? foundingCardRef : undefined}
                data-testid={`plan-${p.key}`}
                className={`relative border-2 ${p.accent} bg-zinc-950/80 rounded-lg p-6 flex flex-col scroll-mt-32`}
              >
                {p.ribbon && !disabled && (
                  <span className="absolute -top-2 right-4 bg-red-600 text-white text-[10px] font-black uppercase tracking-[0.25em] px-2 py-0.5 rounded">
                    {p.ribbon}
                  </span>
                )}
                <h3 className="text-sm font-black uppercase tracking-[0.25em] text-red-400 mb-3">{p.label}</h3>
                <div className="flex items-baseline gap-2 mb-3">
                  <span className="text-4xl font-black text-white leading-none" style={{ fontFamily: 'Georgia, serif' }}>{p.price}</span>
                  <span className="text-xs text-zinc-500 uppercase tracking-widest">{p.priceSuffix}</span>
                </div>
                <p className="text-zinc-400 text-sm mb-6 flex-1 leading-relaxed">{p.tagline}</p>

                {p.key === 'founding' && !disabled && (
                  <div className="mb-4">
                    <label className="block text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5">
                      Optional name or business on the{' '}
                      <Link
                        to="/founders"
                        className="text-yellow-400 hover:text-yellow-300 underline decoration-yellow-500/40"
                        data-testid="founding-founders-link"
                      >
                        Founders page
                      </Link>
                    </label>
                    <input
                      type="text"
                      maxLength={40}
                      value={founderName}
                      onChange={(e) => setFounderName(e.target.value)}
                      data-testid="founder-display-name"
                      className="w-full bg-black border border-zinc-700 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-yellow-500"
                      placeholder="Leave blank to stay anonymous"
                    />
                    <p className="text-[10px] text-zinc-600 mt-1.5">
                      Max 40 characters. Ryan approves it before it goes live. Leave blank and
                      you stay off the wall entirely - we get it, some readers prefer that given
                      what we cover.
                    </p>
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => startCheckout(p.key)}
                  disabled={disabled || busy === p.key}
                  data-testid={`checkout-${p.key}`}
                  className={`inline-flex items-center justify-center gap-2 px-4 py-3 rounded font-black text-xs uppercase tracking-[0.25em] transition-colors ${
                    disabled
                      ? 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
                      : p.key === 'founding'
                      ? 'bg-yellow-500 hover:bg-yellow-400 text-black'
                      : 'bg-red-600 hover:bg-red-500 text-white'
                  }`}
                >
                  {disabled ? (
                    <>
                      <Lock className="h-3.5 w-3.5" /> Founding is closed
                    </>
                  ) : busy === p.key ? (
                    'Opening Stripe…'
                  ) : p.key === 'founding' ? (
                    'Claim a Founding place'
                  ) : (
                    `Start ${p.label}`
                  )}
                </button>

                {p.key === 'founding' && slots && !disabled && (
                  <p className="mt-3 text-[10px] uppercase tracking-[0.25em] text-yellow-500/80">
                    {slots.remaining} of {slots.cap} places left
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {foundingSoldOut && (
          <p className="mt-6 text-center text-sm text-zinc-400">
            <strong>Founding is closed.</strong> Outlaw Access remains open at $13 a month or $130 a year.
          </p>
        )}

        <p className="mt-10 text-center text-xs text-zinc-500">
          Payments processed securely by Stripe. Tax handled automatically. Cancel any time from your account.
        </p>
        <p className="mt-3 text-center text-[11px] font-black uppercase tracking-[0.25em] text-red-400" data-testid="subscribe-sharing-footer">
          One account per person · Any account sharing = ban without refund
        </p>
      </main>
      <SiteFooter />
    </div>
  );
};

export default Subscribe;
